import React from 'react';
import { Form, Badge } from 'react-bootstrap';
import { OrderItem } from '../../../../api/shop/types/orderTypes';
import { OrderStatusBadge, BADGE_COLORS } from '../../../../Common/BadgeStyles';
import ReturnBadge from './ReturnBadge';
import { formatDate, formatPrice } from '../../../../utils/formatUtils';
import '../../../../Common/BadgeStyles.css';
import './OrderCard.css';

interface OrderCardProps {
  order: OrderItem;
  onViewOrder?: (orderId: string, orderItemId: string, orderType?: string) => void;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (isSelected: boolean) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ 
  order, 
  onViewOrder, 
  showCheckbox = false, 
  isSelected = false, 
  onSelectionChange 
}) => {
  // Helper function to format return status for badge display
  const formatReturnStatus = (status: string): string => {
    if (!status) return 'Return';
    // Capitalize first letter, lowercase the rest
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Helper function to format cancellation status for badge display
  const formatCancellationStatus = (status: string): string => {
    if (!status) return 'Cancelled';
    // Capitalize first letter, lowercase the rest
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const handleCardClick = () => {
    if (onViewOrder) {
      const orderStatus = order.status?.toUpperCase();
      if (orderStatus === 'CANCELLED') {
        onViewOrder(order.orderId, order.orderItemId, 'CANCELLED');
        return;
      }
      
      if (order.returnRequest) {
        const returnId = 
          order.returnRequest.returnId || 
          order.returnId ||
          (order.returnRequest as any)?._id ||
          (order.returnRequest as any)?.id;
        
        if (returnId) {
          onViewOrder(returnId, '');
          return;
        }
      }
      
      if (order.returnId) {
        onViewOrder(order.returnId, '');
        return;
      }
      
      onViewOrder(order.orderId, order.orderItemId);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent card click
    if (onSelectionChange) {
      onSelectionChange(e.target.checked);
    }
  };


  return (
    <div 
      className={`order-card ${isSelected ? 'selected' : ''} ${showCheckbox ? 'has-checkbox' : ''}`} 
      onClick={handleCardClick}
    >
      {/* Header Row - Checkbox + Product Name + Badge (same line) */}
      <div className="order-card-header">
        {/* Checkbox (only for CONFIRMED orders) */}
        {showCheckbox && (
          <div className="order-card-checkbox" onClick={(e) => e.stopPropagation()}>
            <Form.Check
              type="checkbox"
              id={`order-checkbox-${order.orderItemId}`}
              checked={isSelected}
              onChange={handleCheckboxChange}
              className="order-checkbox"
            />
          </div>
        )}
        
        {/* Product Name */}
        <h6 className="order-card-product-name">{order.productName}</h6>
        
        <div className="order-card-status" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {order.returnRequest ? (
            <>
              <Badge
                bg=""
                className="shop-order-status-badge"
                style={{
                  backgroundColor: BADGE_COLORS.RETURN.backgroundColor,
                  color: BADGE_COLORS.RETURN.color,
                  borderColor: BADGE_COLORS.RETURN.borderColor,
                  border: `1px solid ${BADGE_COLORS.RETURN.borderColor}`,
                } as React.CSSProperties}
              >
                Return
              </Badge>
              <ReturnBadge 
                returnStatus={order.returnRequest.returnStatus || order.returnStatus} 
                refundStatus={order.refundStatus} 
              />
            </>
          ) : order.returnId ? (
            <>
              <Badge
                bg=""
                className="shop-order-status-badge"
                style={{
                  backgroundColor: BADGE_COLORS.RETURN.backgroundColor,
                  color: BADGE_COLORS.RETURN.color,
                  borderColor: BADGE_COLORS.RETURN.borderColor,
                  border: `1px solid ${BADGE_COLORS.RETURN.borderColor}`,
                } as React.CSSProperties}
              >
                Return
              </Badge>
              <ReturnBadge 
                returnStatus={order.returnStatus} 
                refundStatus={order.refundStatus} 
              />
            </>
          ) : order.status === 'CANCELLED' && order.cancellationStatus ? (
            <Badge
              bg=""
              className="shop-order-status-badge"
              style={{
                backgroundColor: BADGE_COLORS.STATUS.INACTIVE.backgroundColor,
                color: BADGE_COLORS.STATUS.INACTIVE.color,
                borderColor: BADGE_COLORS.STATUS.INACTIVE.borderColor,
                border: `1px solid ${BADGE_COLORS.STATUS.INACTIVE.borderColor}`,
              } as React.CSSProperties}
            >
              {`Cancel - ${formatCancellationStatus(order.cancellationStatus)}`}
            </Badge>
          ) : (
            <OrderStatusBadge status={order.status} />
          )}
        </div>
      </div>
      
      {/* Bottom Section - Customer Name + Price/Date */}
      <div className="order-card-bottom-section">
        {/* Customer Name - Right above price */}
        <p className="order-card-customer-name">{order.customerName}</p>
        
        {/* Price and Date Row */}
        <div className="order-card-bottom">
          <span className="order-card-price">{formatPrice(order.price)}</span>
          <span className="order-card-date">{formatDate(order.orderDate)}</span>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;